import { useCallback, useEffect, useRef, useState } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/20/solid';
import { Combobox } from '@headlessui/react';
import { useNavigate } from 'react-router-dom';
import WorksheetResultList from './WorksheetResultList';

type WorksheetSearchProps = {
    isCollapsed: boolean;
    onExpand: () => void;
    onBlur: () => void;
};

function WorksheetSearch({ isCollapsed, onBlur, onExpand }: WorksheetSearchProps): JSX.Element {
    const inputRef = useRef<HTMLInputElement | null>(null);
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const outerDivRef = useRef<HTMLDivElement>(null);

    const navigate = useNavigate();

    const [collapsed, setCollapsed] = useState(isCollapsed);
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');

    function resetSearch(): void {
        setSearch('');
        setDebouncedSearch('');
        inputRef.current?.blur();
    }

    const handleShortcutKey = useCallback(function handleShortcutKey(event: KeyboardEvent): void {
        // @ts-ignore
        if (!event.target?.contains(inputRef.current)) {
            return;
        }

        const keyPressed = event.key === '/';
        if (keyPressed && buttonRef.current) {
            buttonRef.current.click();
        }
    }, []);

    useEffect(() => {
        document.addEventListener('keydown', handleShortcutKey);
        return (): void => document.removeEventListener('keydown', handleShortcutKey);
    }, [handleShortcutKey]);

    const handleClickOutside = useCallback((event: MouseEvent) => {
        // @ts-ignore
        if (outerDivRef.current && !outerDivRef.current.contains(event.target)) {
            resetSearch();
            onBlur();
        }
    }, []);

    /**
     * Whenever the value changes don't fire setDebouncedSearch until the timeout has completed.
     * every time a new value is entered; reset the timeout
     */
    useEffect(() => {
        if (!search) {
            setDebouncedSearch('');
            return;
        }

        let timeout: NodeJS.Timeout;

        if (search !== undefined) {
            timeout = setTimeout(() => setDebouncedSearch(search), 250);
        }
        return (): void => timeout && clearTimeout(timeout);
    }, [search]);

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);

        return (): void => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [handleClickOutside]);

    useEffect(() => {
        setCollapsed(isCollapsed);
    }, [isCollapsed]);

    return (
        <Combobox
            value={search || ''}
            onChange={(url): void => {
                navigate(url);
                resetSearch();
                onBlur();
            }}
        >
            <div ref={outerDivRef}>
                <Combobox.Button
                    className={`${collapsed ? 'w-8' : 'w-[207px]'} h-8 text-gray-500 dark:text-gray-400`}
                    ref={buttonRef}
                    onClick={onExpand}
                >
                    <div className="h-8 bg-gray-100 dark:bg-white/[0.05] rounded-md px-2 flex items-center gap-1">
                        <MagnifyingGlassIcon className="h-4 w-4 shrink-0 cursor-pointer" />
                        <Combobox.Input
                            ref={inputRef}
                            className={`bg-transparent w-full border-0 outline-none ring-0 text-sm px-1 ${
                                collapsed ? 'opacity-0 pointer-events-none' : ''
                            }`}
                            placeholder="Go to a worksheet..."
                            onChange={(event): void => setSearch(event.target.value.trim())}
                            onBlur={resetSearch}
                            onKeyDown={(e: React.KeyboardEvent): void => {
                                if (e.key === 'Escape') {
                                    resetSearch();
                                    onBlur();
                                }
                            }}
                        />
                        {!collapsed && (
                            <span className="bg-white dark:bg-white/[0.05] inline-flex items-center border border-gray-200 dark:border-white/[0.07] rounded px-2 text-sm font-sans font-medium">
                                <kbd className="font-sans">/</kbd>
                            </span>
                        )}
                    </div>
                </Combobox.Button>
            </div>

            <Combobox.Options className="absolute bg-white dark:bg-gray-800 z-40 max-h-96 overflow-y-auto top-10 border border-gray-200 dark:border-white/[0.07] w-[250px] rounded-md">
                <WorksheetResultList search={debouncedSearch} onNavigate={resetSearch} />
            </Combobox.Options>
        </Combobox>
    );
}

export default WorksheetSearch;
